import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MetalCompositionCreate } from '../types';

interface MetalCompositionFormProps {
  compositions: MetalCompositionCreate[];
  onChange: (compositions: MetalCompositionCreate[]) => void;
  disabled?: boolean;
}

// Häufige Metalle als Vorschläge
const COMMON_METALS = [
  'Kupfer',
  'Eisen',
  'Nickel',
  'Chrom',
  'Mangan',
  'Aluminium',
  'Zinn',
  'Zink',
  'Blei',
  'Molybdän',
  'Vanadium',
  'Titan',
  'Silicium',
  'Phosphor',
  'Schwefel',
  'Kohlenstoff',
  'Stickstoff',
];

const MetalCompositionForm: React.FC<MetalCompositionFormProps> = ({
  compositions,
  onChange,
  disabled = false,
}) => {
  const [errors, setErrors] = useState<string[]>([]);

  const validateCompositions = (newCompositions: MetalCompositionCreate[]): string[] => {
    const validationErrors: string[] = [];

    newCompositions.forEach((comp, index) => {
      if (!comp.metal_name.trim()) {
        validationErrors.push(`Zeile ${index + 1}: Metallname ist erforderlich`);
      }

      if (comp.min_percentage < 0 || comp.min_percentage > 100) {
        validationErrors.push(`Zeile ${index + 1}: Minimum muss zwischen 0 und 100% liegen`);
      }

      if (comp.max_percentage < 0 || comp.max_percentage > 100) {
        validationErrors.push(`Zeile ${index + 1}: Maximum muss zwischen 0 und 100% liegen`);
      }

      if (comp.min_percentage > comp.max_percentage) {
        validationErrors.push(`Zeile ${index + 1}: Minimum darf nicht größer als Maximum sein`);
      }
    });

    // Check for duplicate metals
    const metalNames = newCompositions.map((c) => c.metal_name.toLowerCase().trim());
    const duplicates = metalNames.filter((name, index) => metalNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      validationErrors.push('Metalle dürfen nicht doppelt vorkommen');
    }

    return validationErrors;
  };

  const handleCompositionChange = (
    index: number,
    field: keyof MetalCompositionCreate,
    value: string | number
  ) => {
    const newCompositions = [...compositions];

    if (field === 'metal_name') {
      newCompositions[index][field] = value as string;
    } else {
      newCompositions[index][field] = Number(value);
    }

    const validationErrors = validateCompositions(newCompositions);
    setErrors(validationErrors);
    onChange(newCompositions);
  };

  const addComposition = () => {
    const newComposition: MetalCompositionCreate = {
      metal_name: '',
      min_percentage: 0,
      max_percentage: 0,
    };
    onChange([...compositions, newComposition]);
  };

  const removeComposition = (index: number) => {
    const newCompositions = compositions.filter((_, i) => i !== index);
    const validationErrors = validateCompositions(newCompositions);
    setErrors(validationErrors);
    onChange(newCompositions);
  };

  const getTotalPercentageRange = () => {
    const minTotal = compositions.reduce((sum, comp) => sum + (comp.min_percentage || 0), 0);
    const maxTotal = compositions.reduce((sum, comp) => sum + (comp.max_percentage || 0), 0);
    return { minTotal, maxTotal };
  };

  const { minTotal, maxTotal } = getTotalPercentageRange();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Metall-Zusammensetzung</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addComposition}
          disabled={disabled}
          size="small"
        >
          Metall hinzufügen
        </Button>
      </Box>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {compositions.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
          Keine Metall-Zusammensetzung definiert. Klicken Sie auf "Metall hinzufügen", um zu
          beginnen.
        </Typography>
      ) : (
        <Box>
          {compositions.map((composition, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      value={composition.metal_name}
                      onChange={(_, newValue) => {
                        handleCompositionChange(index, 'metal_name', newValue || '');
                      }}
                      onInputChange={(_, newInputValue) => {
                        handleCompositionChange(index, 'metal_name', newInputValue);
                      }}
                      options={COMMON_METALS}
                      freeSolo
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Metall"
                          required
                          error={!composition.metal_name.trim() && compositions.length > 0}
                          helperText={
                            !composition.metal_name.trim() && compositions.length > 0
                              ? 'Erforderlich'
                              : ''
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      label="Min %"
                      value={composition.min_percentage}
                      onChange={(e) =>
                        handleCompositionChange(index, 'min_percentage', e.target.value)
                      }
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      disabled={disabled}
                      error={
                        composition.min_percentage < 0 ||
                        composition.min_percentage > 100 ||
                        composition.min_percentage > composition.max_percentage
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      label="Max %"
                      value={composition.max_percentage}
                      onChange={(e) =>
                        handleCompositionChange(index, 'max_percentage', e.target.value)
                      }
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      disabled={disabled}
                      error={
                        composition.max_percentage < 0 ||
                        composition.max_percentage > 100 ||
                        composition.min_percentage > composition.max_percentage
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <IconButton
                      onClick={() => removeComposition(index)}
                      disabled={disabled}
                      color="error"
                      title="Metall entfernen"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {compositions.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Gesamtbereich:</strong> {minTotal.toFixed(2)}% - {maxTotal.toFixed(2)}%
              </Typography>
              {(minTotal > 100 || maxTotal > 100) && (
                <Typography variant="body2" color="error">
                  ⚠️ Warnung: Gesamtsumme überschreitet 100%
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MetalCompositionForm;
